import { FluentBundle, FluentResource } from '@fluent/bundle';

const SUPPORTED_LOCALES = ['en', 'fr', 'it'];
const FTL_FILES = ['main.ftl', 'stages.ftl'];

function detectLocale(): string {
  // Check ?lang= query parameter first
  const params = new URLSearchParams(window.location.search);
  const langParam = params.get('lang');
  if (langParam && SUPPORTED_LOCALES.includes(langParam)) {
    return langParam;
  }
  // Fall back to browser language
  const prefix = navigator.language?.split(/[-_]/)[0] ?? 'en';
  return SUPPORTED_LOCALES.includes(prefix) ? prefix : 'en';
}

const LOCALE = detectLocale();

let bundle: FluentBundle | null = null;
let loadPromise: Promise<void> | null = null;
const listeners = new Set<() => void>();

async function loadBundle(): Promise<void> {
  const b = new FluentBundle(LOCALE);
  const results = await Promise.all(
    FTL_FILES.map((f) =>
      fetch(`/locales/${LOCALE}/${f}`).then((r) => r.text()),
    ),
  );
  for (const ftl of results) {
    b.addResource(new FluentResource(ftl));
  }
  bundle = b;
  for (const cb of listeners) cb();
}

/** Start loading locale data. Safe to call multiple times. */
export function initI18n(): Promise<void> {
  if (!loadPromise) {
    loadPromise = loadBundle();
  }
  return loadPromise;
}

/** Subscribe to i18n ready event. Returns unsubscribe function. */
export function onI18nReady(cb: () => void): () => void {
  if (bundle) {
    cb();
  } else {
    listeners.add(cb);
  }
  return () => listeners.delete(cb);
}

/** Whether the bundle has loaded. */
export function i18nReady(): boolean {
  return bundle !== null;
}

/** Translate a key. Returns empty string if not yet loaded. */
export function t(id: string): string {
  if (!bundle) return '';
  const msg = bundle.getMessage(id);
  if (!msg?.value) return id;
  return bundle.formatPattern(msg.value);
}

/** Get stage display name. Falls back to slug. */
export function stageName(slug: string): string {
  return t(`stage-${slug}`) || slug;
}

/** Get stage comment. Returns null if not found. */
export function stageComment(slug: string, isTouch: boolean): string | null {
  if (isTouch) {
    const touch = tryGet(`touch-comment-${slug}`);
    if (touch) return touch;
  }
  return tryGet(`comment-${slug}`);
}

function tryGet(id: string): string | null {
  if (!bundle) return null;
  const msg = bundle.getMessage(id);
  if (!msg?.value) return null;
  return bundle.formatPattern(msg.value);
}
